'use client'

import { useState, useEffect, useRef } from 'react'
import AirportCombobox from '@/components/AirportCombobox'
import { QRCodeSVG } from 'qrcode.react'
import { useAppData } from '@/context/AppDataContext'

async function saveToApi(accessToken, data) {
  const res = await fetch('/api/profile', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error ?? `Save failed (${res.status})`)
  }
}

// ── Inline step forms ─────────────────────────────────────────────────────────

function NameStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const [firstName, setFirstName] = useState(profile?.firstName ?? currentUser?.displayName?.split(' ')[0] ?? '')
  const [lastName, setLastName]   = useState(profile?.lastName  ?? currentUser?.displayName?.split(' ').slice(1).join(' ') ?? '')
  const [nickname, setNickname]   = useState(profile?.nickname ?? '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    setFirstName(profile?.firstName ?? currentUser?.displayName?.split(' ')[0] ?? '')
    setLastName(profile?.lastName   ?? currentUser?.displayName?.split(' ').slice(1).join(' ') ?? '')
    setNickname(profile?.nickname   ?? '')
  }, [profile?.firstName, profile?.lastName, profile?.nickname])

  async function save() {
    const payload = { firstName: firstName.trim(), lastName: lastName.trim(), nickname: nickname.trim() }
    setSaving(true)
    setSaveError(null)
    try {
      await saveToApi(accessToken, payload)
      onUpdate(payload)
      onDone()
    } catch (e) {
      setSaveError(e.message ?? 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="First name"
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-400"
        />
        <input
          type="text"
          placeholder="Last name"
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-400"
        />
      </div>
      <div>
        <input
          type="text"
          placeholder="Nickname (optional) — used in app, email & Telegram"
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-blue-400"
        />
        <p className="text-xs text-gray-400 mt-1 px-1">Leave empty to use your first name</p>
      </div>
      {saveError && <p className="text-xs text-red-500">{saveError}</p>}
      <StepBtn onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </StepBtn>
      <UpdateLater href="/app/settings" label="Settings" />
    </div>
  )
}

function AirportStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const { deals: ctxDeals, destinations: ctxDests } = useAppData()
  const tier = profile?.tier ?? 'free'
  const limit = tier === 'pro' ? 15 : tier === 'plus' ? 5 : 1
  const [airports, setAirports] = useState(() => (profile?.homeAirports ?? []).slice(0, limit))
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // Airport change limits per calendar month
  const CHANGE_LIMITS = { free: 2, plus: 3, pro: 5 }
  const changeLimit = CHANGE_LIMITS[tier] ?? 2
  const ym = new Date().toISOString().slice(0, 7)
  const changesThisMonth = (profile?.homeAirportChangeDates ?? []).filter(d => d.startsWith(ym)).length
  const changeLimitReached = changesThisMonth >= changeLimit

  const destMap = Object.fromEntries((ctxDests ?? []).map(d => [d.code, d.city_name]))
  const allDeals = ctxDeals ?? []

  function buildPreview(ap) {
    const apDeals    = allDeals.filter(d => d.origin === ap)
    const visible    = apDeals.filter(d => !d._locked)
    const lockedCount = apDeals.filter(d => d._locked).length
    const topDests   = [...new Set(visible.map(d => d.destination))]
      .slice(0, 4)
      .map(code => destMap[code] ?? code)
    return { count: visible.length, lockedCount, destinations: topDests }
  }

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      await saveToApi(accessToken, { homeAirports: airports })
      const today = new Date().toISOString().slice(0, 10)
      onUpdate({
        homeAirports: airports,
        homeAirportChangeDates: [...(profile?.homeAirportChangeDates ?? []), today],
      })
      onDone()
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      {airports.length > 0 && (
        <div className="space-y-2">
          {airports.map(ap => {
            return (
              <div key={ap} className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-blue-700">{ap}</span>
                  <button type="button" onClick={() => { setAirports(airports.filter(a => a !== ap)) }} className="text-blue-400 hover:text-blue-700 text-sm leading-none">×</button>
                </div>
                {(() => { const p = buildPreview(ap); return p.count > 0 ? (
                    <p className="text-xs text-blue-600 mt-1">
                      ✈ <strong>{p.count} deal{p.count !== 1 ? 's' : ''}</strong> available
                      {p.destinations.length > 0 && <> — {p.destinations.join(', ')}{p.destinations.length === 4 ? ' & more' : ''}</>}
                      {p.lockedCount > 0 && <> · <a href="/app/subscription" onClick={e => e.stopPropagation()} className="font-semibold text-blue-500 hover:underline">+{p.lockedCount} with Plus/Pro ↗</a></>}
                    </p>
                  ) : (
                    <p className="text-xs text-blue-400 mt-1">No deals from {ap} right now — check back soon.</p>
                  ) })()}
              </div>
            )
          })}
        </div>
      )}

      {airports.length < limit && (
        <AirportCombobox
          value=""
          exclude={airports}
          onChange={(iata) => { if (iata && !airports.includes(iata)) setAirports([...airports, iata]) }}
          placeholder="Search airport…"
          clearable={false}
        />
      )}

      <p className="text-xs text-gray-400">
        {airports.length}/{limit} home airport{limit !== 1 ? 's' : ''} set for your {tier.charAt(0).toUpperCase() + tier.slice(1)} account.
        {tier === 'free' && <> <a href="/app/subscription" className="text-blue-500 hover:underline">Upgrade</a> for up to 5 or 15.</>}
      </p>
      <p className={`text-xs ${changeLimitReached ? 'text-red-500' : 'text-gray-400'}`}>
        {changesThisMonth}/{changeLimit} airport changes used this month.
        {changeLimitReached && tier !== 'pro' && <> <a href="/app/subscription" className="text-blue-500 hover:underline">Upgrade</a> to change more often.</>}
      </p>
      {saveError && <p className="text-xs text-red-500">{saveError}</p>}
      <StepBtn onClick={save} disabled={airports.length === 0 || saving || changeLimitReached}>
        {saving ? 'Saving…' : 'Save airport'}
      </StepBtn>
      <UpdateLater href="/app" label="Home" />
    </div>
  )
}

const TIER_RANK = { free: 0, plus: 1, pro: 2 }
function tierAllows(userTier, required) {
  return (TIER_RANK[userTier] ?? 0) >= (TIER_RANK[required] ?? 0)
}

function NotificationsStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const tier = profile?.tier ?? 'free'
  const [freq, setFreq] = useState(profile?.alertFrequency ?? 'weekly')
  const [alertHour, setAlertHour] = useState(profile?.alertHour ?? 8)
  const [quietEnabled, setQuietEnabled] = useState(!!(profile?.quietFrom))
  const [quietFrom, setQuietFrom] = useState(profile?.quietFrom ?? '23:00')
  const [quietTo, setQuietTo] = useState(profile?.quietTo ?? '07:00')
  const [alertOnFilterMatch, setAlertOnFilterMatch] = useState(profile?.alertOnFilterMatch ?? true)
  const [tgAlerts, setTgAlerts] = useState(profile?.telegramAlerts ?? 'off')
  const [copiedTg, setCopiedTg] = useState(false)
  const [saving, setSaving] = useState(false)
  const [tgConnected, setTgConnected] = useState(!!profile?.telegramChatId)
  const botUrl = `https://t.me/FairFaresDealsBot?start=${currentUser?.uid ?? ''}`

  // Poll for Telegram connection after user clicks the bot link
  useEffect(() => {
    if (tgConnected) return
    let tries = 0
    const iv = setInterval(async () => {
      tries++
      if (tries > 20) { clearInterval(iv); return } // stop after ~40s
      try {
        const r = await fetch('/api/profile', { headers: { Authorization: `Bearer ${accessToken}` } })
        const p = await r.json()
        if (p?.telegramChatId) { setTgConnected(true); onUpdate(p); clearInterval(iv) }
      } catch {}
    }, 2000)
    return () => clearInterval(iv)
  }, [tgConnected, accessToken, onUpdate])

  const opts = [
    { value: 'instant', label: 'Instant',       desc: 'Every new deal, right away',  requires: 'pro'  },
    { value: 'daily',   label: 'Daily digest',   desc: 'Best deals of the day',       requires: 'plus' },
    { value: 'weekly',  label: 'Weekly digest',  desc: 'Top picks once a week',       requires: 'free' },
    { value: 'none',    label: 'No emails',      desc: "I'll check the app myself",  requires: 'free' },
  ]

  async function save() {
    setSaving(true)
    try {
      const payload = { alertFrequency: freq }
      if (freq === 'daily') payload.alertHour = alertHour
      payload.quietFrom = quietEnabled ? quietFrom : null
      payload.quietTo   = quietEnabled ? quietTo   : null
      payload.alertOnFilterMatch = alertOnFilterMatch
      payload.telegramAlerts = tgAlerts
      await saveToApi(accessToken, payload)
      onUpdate(payload)
      onDone()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  const BADGE = { pro: { label: 'Pro', cls: 'bg-amber-100 text-amber-700' }, plus: { label: 'Plus', cls: 'bg-blue-100 text-blue-600' } }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-gray-700">How often do you want email notifications?</p>
        <div className="flex flex-col gap-1.5">
          {opts.map(o => {
            const allowed = tierAllows(tier, o.requires)
            const selected = freq === o.value
            const badge = !allowed && BADGE[o.requires]
            return (
              <div key={o.value}>
                <button
                  type="button"
                  onClick={() => allowed && setFreq(o.value)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs border transition-colors text-left ${
                    selected && allowed ? 'bg-blue-50 border-blue-400 text-blue-800'
                    : allowed ? 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    : 'bg-gray-50 border-gray-100 text-gray-300 cursor-default'
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${selected && allowed ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                    {selected && allowed && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
                  </span>
                  <span className="flex-1">
                    <span className="font-medium">{o.label}</span>
                    <span className="text-gray-400 ml-1.5">{o.desc}</span>
                  </span>
                  {badge && (
                    <a href="/app/subscription" onClick={e => e.stopPropagation()} className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${badge.cls} hover:opacity-80 transition-opacity`}>
                      {badge.label} ↗
                    </a>
                  )}
                </button>
                {o.value === 'daily' && selected && allowed && (
                  <div className="mt-1.5 ml-9 flex items-center gap-1.5">
                    <span className="text-xs text-gray-500">around</span>
                    <select
                      value={alertHour}
                      onChange={e => setAlertHour(Number(e.target.value))}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:border-blue-400"
                    >
                      {[6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21].map(h => (
                        <option key={h} value={h}>{h}:00</option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-500">o'clock</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      {/* Filter match alerts */}
      <label className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors">
        <input
          type="checkbox"
          checked={alertOnFilterMatch}
          onChange={e => setAlertOnFilterMatch(e.target.checked)}
          className="accent-blue-600 w-4 h-4 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700">Alert me on filter matches</p>
          <p className="text-xs text-gray-400">Instant email when a deal matches your wishlist or preferences</p>
        </div>
      </label>

      {/* Travel window alerts — Plus */}
      <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 ${tierAllows(tier, 'plus') ? '' : 'opacity-60'}`}>
        <input type="checkbox" disabled className="accent-blue-600 w-4 h-4 shrink-0 opacity-40" checked={tierAllows(tier, 'plus')} readOnly />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700">
            Travel window alerts
            {!tierAllows(tier, 'plus') && (
              <a href="/app/subscription" className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:opacity-80">Plus ↗</a>
            )}
          </p>
          <p className="text-xs text-gray-400">Priority alert when a deal matches your availability windows</p>
        </div>
      </div>

      {/* Quiet hours — Pro only */}
      <div className={`rounded-xl border overflow-hidden ${tier === 'pro' ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
        <button
          type="button"
          onClick={() => tier === 'pro' && setQuietEnabled(v => !v)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${tier === 'pro' ? 'hover:bg-gray-50' : 'cursor-default'}`}
        >
          <div className="flex-1">
            <span className="text-sm text-gray-700">Quiet hours</span>
            {tier !== 'pro'
              ? <a href="/app/subscription" onClick={e => e.stopPropagation()} className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:opacity-80">Pro ↗</a>
              : !quietEnabled && <span className="ml-2 text-xs text-gray-400">off</span>
            }
          </div>
          <div className={`relative rounded-full transition-colors flex-shrink-0`} style={{width:'32px',height:'18px',background: quietEnabled && tier === 'pro' ? '#3b82f6' : '#e5e7eb'}}>
            <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${quietEnabled && tier === 'pro' ? 'translate-x-3.5' : 'translate-x-0.5'}`} style={{width:'14px',height:'14px'}} />
          </div>
        </button>
        {quietEnabled && tier === 'pro' && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-50 space-y-2">
            <p className="text-xs text-gray-400">No emails sent during these hours.</p>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-xs text-gray-400 block mb-1">From</label>
                <input
                  type="time"
                  value={quietFrom}
                  onChange={e => setQuietFrom(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 focus:outline-none focus:border-blue-400"
                />
              </div>
              <span className="text-xs text-gray-400 mt-4">→</span>
              <div className="flex-1">
                <label className="text-xs text-gray-400 block mb-1">Until</label>
                <input
                  type="time"
                  value={quietTo}
                  onChange={e => setQuietTo(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Telegram */}
      <div className="rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-3 py-2.5 flex items-center gap-2 bg-gray-50/60">
          <svg className="w-4 h-4 text-blue-500 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.19 13.6l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.958.96z"/></svg>
          <p className="text-sm font-medium text-gray-700 flex-1">Telegram</p>
          {tgConnected
            ? <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Connected ✓</span>
            : <span className="text-xs text-gray-400">Not connected</span>
          }
        </div>
        <div className="px-3 py-3 space-y-3">
          <p className="text-xs text-gray-500">
            Get deal alerts on Telegram — same as email but faster. Free, no phone number needed.
          </p>
          {!tgConnected && (
            <div className="space-y-2">
              <ol className="space-y-1.5 text-xs text-gray-600">
                <li className="flex gap-2"><span className="font-bold text-blue-500 shrink-0">1.</span>Use the button or scan the QR code — the link contains your account ID</li>
                <li className="flex gap-2"><span className="font-bold text-blue-500 shrink-0">2.</span>Tap <code className="bg-gray-100 px-1 rounded">Start</code> in Telegram — your account links automatically</li>
              </ol>
              <div className="flex items-start gap-3">
                <a href={botUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg transition-colors">
                  Open Telegram →
                </a>
                <div className="flex flex-col items-center gap-1">
                  <div className="p-1.5 bg-white border border-gray-200 rounded-lg">
                    <QRCodeSVG value={botUrl} size={80} bgColor="#ffffff" fgColor="#111827" level="M" />
                  </div>
                  <span className="text-[10px] text-gray-400">scan met telefoon</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                New to Telegram?{' '}
                <a href="https://apps.apple.com/app/telegram-messenger/id686449807" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">App Store</a>
                {' '}·{' '}
                <a href="https://play.google.com/store/apps/details?id=org.telegram.messenger" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Google Play</a>
              </p>
            </div>
          )}
          {tgConnected && (
            <div className="space-y-2 pt-1 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-700">How often do you want deal alerts?</p>
              <div className="flex flex-col gap-1.5">
                {[
                  { value: 'off',       label: 'Off',          desc: 'No notifications' },
                  { value: 'daily',     label: 'Daily digest', desc: tier === 'plus' ? 'Best deals on your routes' : 'Best deals of the day · around 8:00' },
                  { value: 'real-time', label: 'Real-time',    desc: 'Instant — the moment we find one', requires: 'pro' },
                ].map(o => {
                  const allowed = !o.requires || tierAllows(tier, o.requires)
                  const selected = tgAlerts === o.value
                  return (
                    <button key={o.value} type="button"
                      onClick={() => allowed && setTgAlerts(o.value)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs border transition-colors text-left ${
                        selected ? 'bg-blue-50 border-blue-400 text-blue-800'
                        : allowed ? 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        : 'bg-gray-50 border-gray-100 text-gray-300 cursor-default'
                      }`}>
                      <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                        {selected && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
                      </span>
                      <span className="flex-1">
                        <span className="font-medium">{o.label}</span>
                        <span className="text-gray-400 ml-1.5">{o.desc}</span>
                      </span>
                      {!allowed && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Pro</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <StepBtn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save notifications'}</StepBtn>
      <UpdateLater href="/app/alerts" label="Alerts" />
    </div>
  )
}

function Counter({ label, sub, value, onChange, min = 0 }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm text-gray-700">{label}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
          className="w-7 h-7 rounded-full border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 text-lg leading-none flex items-center justify-center transition-colors">−</button>
        <span className="w-4 text-center text-sm font-medium text-gray-800">{value}</span>
        <button type="button" onClick={() => onChange(value + 1)}
          className="w-7 h-7 rounded-full border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 text-lg leading-none flex items-center justify-center transition-colors">+</button>
      </div>
    </div>
  )
}

function CompanionStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const [val, setVal]         = useState(profile?.travelCompanion ?? '')
  const [adults, setAdults]   = useState(profile?.travelAdults   ?? 1)
  const [children, setChildren] = useState(profile?.travelChildren ?? 0)
  const [babies, setBabies]   = useState(profile?.travelBabies   ?? 0)
  const [saving, setSaving]   = useState(false)

  const opts = [
    { value: 'solo',   label: 'Solo' },
    { value: 'couple', label: 'Couple' },
    { value: 'group',  label: 'Family / Friends' },
  ]

  const showPicker = val === 'group'

  async function save() {
    setSaving(true)
    const payload = { travelCompanion: val }
    if (showPicker) {
      payload.travelAdults   = adults
      payload.travelChildren = children
      payload.travelBabies   = babies
    }
    try {
      await saveToApi(accessToken, payload)
      onUpdate(payload)
      onDone()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {opts.map(o => (
          <button key={o.value} type="button" onClick={() => setVal(o.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${val === o.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}>
            {o.label}
          </button>
        ))}
      </div>

      {showPicker && (
        <div className="rounded-xl border border-gray-100 px-3 divide-y divide-gray-50">
          <Counter label="Adults" sub="18+ (including yourself)" value={adults} onChange={setAdults} min={1} />
          <Counter label="Children" sub="2–17 years" value={children} onChange={setChildren} />
          <Counter label="Babies" sub="0–1 years" value={babies} onChange={setBabies} />
        </div>
      )}

      <StepBtn onClick={save} disabled={!val || saving}>{saving ? 'Saving…' : 'Save'}</StepBtn>
      <UpdateLater href="/app/settings" label="Settings" />
    </div>
  )
}

function RegionsStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const tier = profile?.tier ?? 'free'
  const [regions, setRegions] = useState(profile?.preferredRegions ?? [])
  const [saving, setSaving] = useState(false)

  const opts = [
    { value: 'europe',      label: 'Europe',      requires: 'free' },
    { value: 'middle_east', label: 'Middle East', requires: 'plus' },
    { value: 'asia',        label: 'Asia',        requires: 'pro'  },
    { value: 'americas',    label: 'Americas',    requires: 'pro'  },
    { value: 'africa',      label: 'Africa',      requires: 'pro'  },
    { value: 'oceania',     label: 'Oceania',     requires: 'pro'  },
  ]

  const BADGE = {
    plus: { cls: 'bg-blue-100 text-blue-700',   label: 'Plus' },
    pro:  { cls: 'bg-amber-100 text-amber-700', label: 'Pro'  },
  }

  function toggle(v, allowed) {
    if (!allowed) return
    setRegions(r => r.includes(v) ? r.filter(x => x !== v) : [...r, v])
  }

  async function save() {
    setSaving(true)
    try {
      await saveToApi(accessToken, { preferredRegions: regions })
      onUpdate({ preferredRegions: regions })
      onDone()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {opts.map(o => {
          const allowed = tierAllows(tier, o.requires)
          const selected = allowed && regions.includes(o.value)
          const badge = !allowed && BADGE[o.requires]
          return (
            <button key={o.value} type="button"
              onClick={() => toggle(o.value, allowed)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                selected
                  ? 'bg-blue-600 text-white border-blue-600'
                  : allowed
                    ? 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    : 'bg-gray-50 text-gray-400 border-gray-100 cursor-default'
              }`}>
              {o.label}
              {badge && (
                <a href="/app/subscription" onClick={e => e.stopPropagation()}
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badge.cls} hover:opacity-80`}>
                  {badge.label} ↗
                </a>
              )}
            </button>
          )
        })}
      </div>
      <p className="text-xs text-gray-400">Your selection pre-filters the deals page — select none to see all regions. We'll use this to improve your recommendations over time.</p>
      <StepBtn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</StepBtn>
      <UpdateLater href="/app/destinations" label="Destinations" />
    </div>
  )
}

const FMT = (d) => d.toISOString().slice(0, 10)
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const DURATION_OPTS = [
  { key: 'weekend', label: 'Weekend',  days: 2  },
  { key: '1_week',  label: '1 week',   days: 7  },
  { key: '2_weeks', label: '2 weeks',  days: 14 },
  { key: '3_weeks', label: '3 weeks',  days: 21 },
]

function generateSlots(type) {
  const today = new Date(); today.setHours(0,0,0,0)
  const slots = []

  if (type === 'weekend') {
    const d = new Date(today)
    d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7))
    for (let i = 0; i < 16; i++) {
      const start = new Date(d)
      const end = new Date(d); end.setDate(end.getDate() + 1)
      slots.push({ start, end })
      d.setDate(d.getDate() + 7)
    }
  } else if (type === '1_week') {
    const d = new Date(today)
    d.setDate(d.getDate() + ((1 - d.getDay() + 7) % 7 || 7))
    for (let i = 0; i < 16; i++) {
      const start = new Date(d)
      const end = new Date(d); end.setDate(end.getDate() + 6)
      slots.push({ start, end })
      d.setDate(d.getDate() + 7)
    }
  } else {
    // 2_weeks / 3_weeks — show months
    const days = type === '2_weeks' ? 14 : 21
    const d = new Date(today.getFullYear(), today.getMonth(), 1)
    for (let i = 0; i < 9; i++) {
      const start = new Date(d)
      const end = new Date(d); end.setDate(end.getDate() + days - 1)
      slots.push({ start, end })
      d.setMonth(d.getMonth() + 1)
    }
  }
  return slots
}

function AvailabilityStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const [durationType, setDurationType] = useState('weekend')
  const [windows, setWindows] = useState(profile?.travelWindows ?? [])
  const [specificFrom, setSpecificFrom] = useState('')
  const [specificTo, setSpecificTo] = useState('')
  const [saving, setSaving] = useState(false)

  const slots = generateSlots(durationType)

  function toggle(start, end) {
    const s = FMT(start), e = FMT(end)
    const exists = windows.findIndex(w => w.start === s && w.end === e)
    if (exists !== -1) setWindows(windows.filter((_, i) => i !== exists))
    else setWindows([...windows, { start: s, end: e, type: durationType }])
  }

  function addSpecific() {
    if (!specificFrom || !specificTo || specificFrom >= specificTo) return
    const exists = windows.some(w => w.start === specificFrom && w.end === specificTo)
    if (!exists) setWindows([...windows, { start: specificFrom, end: specificTo, type: 'specific' }])
    setSpecificFrom(''); setSpecificTo('')
  }

  function removeWindow(i) {
    setWindows(windows.filter((_, idx) => idx !== i))
  }

  // Group slots by month
  const byMonth = {}
  slots.forEach(({ start, end }) => {
    const key = `${start.getFullYear()}-${start.getMonth()}`
    if (!byMonth[key]) byMonth[key] = { label: `${MONTHS[start.getMonth()]} ${start.getFullYear()}`, slots: [] }
    byMonth[key].slots.push({ start, end })
  })

  function slotLabel({ start, end }) {
    if (durationType === 'weekend' || durationType === '1_week') return `${start.getDate()}–${end.getDate()}`
    return `${MONTHS[start.getMonth()]}`
  }

  function windowDisplay(w) {
    if (w.type === 'specific') {
      const s = new Date(w.start), e = new Date(w.end)
      return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]}`
    }
    const opt = DURATION_OPTS.find(o => o.key === w.type)
    const s = new Date(w.start)
    return `${s.getDate()} ${MONTHS[s.getMonth()]}${opt ? ` (${opt.label})` : ''}`
  }

  async function save() {
    setSaving(true)
    try {
      await saveToApi(accessToken, { travelWindows: windows })
      onUpdate({ travelWindows: windows })
      onDone()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  const flexWindows   = windows.filter(w => w.type !== 'specific')
  const specificWindows = windows.filter(w => w.type === 'specific')

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Optional. You'll still see all deals. We use this to send a priority alert when a deal matches your window — with a few days' flexibility either way.</p>

      {/* Flexible periods */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-700">Flexible windows <span className="font-normal text-gray-400">— we match ±3 days</span></p>
        <div className="flex gap-1.5 flex-wrap">
          {DURATION_OPTS.map(o => (
            <button key={o.key} type="button" onClick={() => setDurationType(o.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${durationType === o.key ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {Object.values(byMonth).map(({ label, slots: ms }) => (
            <div key={label}>
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <div className="flex flex-wrap gap-1.5">
                {ms.map(({ start, end }) => {
                  const s = FMT(start), e = FMT(end)
                  const active = windows.some(w => w.start === s && w.end === e)
                  return (
                    <button key={s} type="button" onClick={() => toggle(start, end)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                      {slotLabel({ start, end })}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Specific holiday dates */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-700">Specific holidays <span className="font-normal text-gray-400">— exact dates you're free</span></p>
        <div className="flex items-center gap-2">
          <input type="date" value={specificFrom} onChange={e => setSpecificFrom(e.target.value)}
            className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 focus:outline-none focus:border-blue-400" />
          <span className="text-xs text-gray-400">→</span>
          <input type="date" value={specificTo} min={specificFrom} onChange={e => setSpecificTo(e.target.value)}
            className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 focus:outline-none focus:border-blue-400" />
          <button type="button" onClick={addSpecific} disabled={!specificFrom || !specificTo || specificFrom >= specificTo}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-30 px-1">Add</button>
        </div>
        {specificWindows.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {specificWindows.map((w, i) => (
              <span key={i} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-2.5 py-1">
                {windowDisplay(w)}
                <button type="button" onClick={() => removeWindow(windows.indexOf(w))} className="text-blue-400 hover:text-blue-700 leading-none ml-0.5">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {windows.length > 0 && (
        <p className="text-xs text-blue-600">{flexWindows.length} flexible + {specificWindows.length} specific window{windows.length !== 1 ? 's' : ''}</p>
      )}
      <StepBtn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</StepBtn>
      <UpdateLater href="/app/plan" label="Planning" />
    </div>
  )
}

function DestinationsStep({ profile, currentUser, accessToken, onUpdate, onDone }) {
  const tier = profile?.tier ?? 'free'
  const wishlistLimit = tier === 'pro' ? Infinity : tier === 'plus' ? 10 : 0

  // Regions state
  const [regions, setRegions] = useState(profile?.preferredRegions ?? [])
  // Wishlist state
  const [wishlist, setWishlist] = useState(profile?.wishlist ?? [])
  const [saving, setSaving] = useState(false)

  const regionOpts = [
    { value: 'europe',      label: 'Europe',      requires: 'free' },
    { value: 'middle_east', label: 'Middle East', requires: 'plus' },
    { value: 'asia',        label: 'Asia',        requires: 'pro'  },
    { value: 'americas',    label: 'Americas',    requires: 'pro'  },
    { value: 'africa',      label: 'Africa',      requires: 'pro'  },
    { value: 'oceania',     label: 'Oceania',     requires: 'pro'  },
  ]
  const BADGE = {
    plus: { cls: 'bg-blue-100 text-blue-700',   label: 'Plus' },
    pro:  { cls: 'bg-amber-100 text-amber-700', label: 'Pro'  },
  }

  function toggleRegion(v, allowed) {
    if (!allowed) return
    setRegions(r => r.includes(v) ? r.filter(x => x !== v) : [...r, v])
  }

  async function save() {
    setSaving(true)
    try {
      await saveToApi(accessToken, { preferredRegions: regions, wishlist })
      onUpdate({ preferredRegions: regions, wishlist })
      onDone()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Regions */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-700">Preferred regions <span className="font-normal text-gray-400">— pre-filters your deals page</span></p>
        <div className="flex flex-wrap gap-2">
          {regionOpts.map(o => {
            const allowed = tierAllows(tier, o.requires)
            const selected = allowed && regions.includes(o.value)
            const badge = !allowed && BADGE[o.requires]
            return (
              <button key={o.value} type="button"
                onClick={() => toggleRegion(o.value, allowed)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  selected ? 'bg-blue-600 text-white border-blue-600'
                  : allowed ? 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                  : 'bg-gray-50 text-gray-400 border-gray-100 cursor-default'
                }`}>
                {o.label}
                {badge && (
                  <a href="/app/subscription" onClick={e => e.stopPropagation()}
                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badge.cls} hover:opacity-80`}>
                    {badge.label} ↗
                  </a>
                )}
              </button>
            )
          })}
        </div>
        <p className="text-xs text-gray-400">Select none to see all regions.</p>
      </div>

      {/* Wishlist */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-700">
          Specific destinations
          {wishlistLimit === 0
            ? <a href="/app/subscription" className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:opacity-80">Plus ↗</a>
            : <span className="font-normal text-gray-400"> — get an alert when a deal appears</span>
          }
        </p>
        {wishlistLimit > 0 ? (
          <AirportCombobox
            value={wishlist}
            onChange={setWishlist}
            max={wishlistLimit}
            placeholder="Search city or country…"
            destinationsOnly
          />
        ) : (
          <p className="text-xs text-gray-400">Upgrade to Plus to add specific destinations.</p>
        )}
      </div>

      <StepBtn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save destinations'}</StepBtn>
    </div>
  )
}

function ReferralStep({ profile }) {
  const tier = profile?.tier ?? 'free'

  const planLabel = tier === 'pro' ? 'Pro' : 'Plus'

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 leading-relaxed">
        Know someone who loves to travel? Share your link. Your friend gets <strong>1 free {planLabel} month</strong> when they finish setup — and so do you (first 3 friends). Every friend who pays adds another month, up to <strong>24 months total</strong>.
      </p>
      <a href="/app/subscription" className="inline-block text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-xl transition-colors">
        Get my referral link →
      </a>
    </div>
  )
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function UpdateLater({ href, label }) {
  return (
    <p className="text-xs text-gray-400 pt-1">
      You can always update this in{' '}
      <a href={href} className="text-gray-500 hover:text-blue-600 underline underline-offset-2 transition-colors">{label} →</a>
    </p>
  )
}

function StepBtn({ onClick, disabled, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors">
      {children}
    </button>
  )
}

function CheckIcon({ className = '' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
    </svg>
  )
}

// ── Phase / step config ───────────────────────────────────────────────────────

const PHASES = [
  {
    id: 'basic',
    label: 'Start with the basics',
    subtitle: 'Where do you fly from, and how do you want to be notified?',
    steps: [
      {
        id: 'name',
        title: 'Your name',
        done: (p) => !!(p?.firstName || p?.lastName),
        Form: NameStep,
      },
      {
        id: 'airport',
        title: 'Set your home airport',
        done: (p) => (p?.homeAirports?.length ?? 0) > 0,
        Form: AirportStep,
      },
      {
        id: 'notifications',
        title: 'Notifications',
        done: (p) => p?.alertFrequency != null,
        Form: NotificationsStep,
      },
    ],
  },
  {
    id: 'personalize',
    label: 'Personalize your account',
    subtitle: 'Help us show deals that match your travel style.',
    steps: [
      {
        id: 'companion',
        title: 'Who do you travel with?',
        done: (p) => !!p?.travelCompanion,
        Form: CompanionStep,
      },
      {
        id: 'destinations',
        title: 'Where do you want to go?',
        done: (p) => Array.isArray(p?.preferredRegions),
        Form: DestinationsStep,
      },
      {
        id: 'vacation',
        title: 'When are you available to travel?',
        done: (p) => Array.isArray(p?.travelWindows),
        Form: AvailabilityStep,
      },
    ],
  },
  {
    id: 'further',
    label: 'Share & earn',
    subtitle: 'Love FairFares? Share it — every referral earns you a free month or extends your plan.',
    steps: [
      {
        id: 'referral',
        title: 'Refer friends, earn free months',
        done: (p) => (p?.referralCount ?? 0) > 0,
        Form: ReferralStep,
      },
    ],
  },
]

export function isOnboardingDone(profile) {
  return PHASES.every(phase => phase.steps.every(s => s.done(profile)))
}

export function getOnboardingProgress(profile) {
  const allSteps = PHASES.flatMap(p => p.steps)
  const done = allSteps.filter(s => s.done(profile)).length
  return { done, total: allSteps.length }
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OnboardingChecklist({ profile, currentUser, accessToken, onUpdate, initialOpen }) {
  // Find which phase contains initialOpen step (if any)
  const initialPhaseIdx = initialOpen != null
    ? PHASES.findIndex(p => p.steps.some(s => s.id === initialOpen))
    : null

  const [expandedStep, setExpandedStep] = useState(initialOpen ?? null)
  const [expandedPhase, setExpandedPhase] = useState(initialPhaseIdx ?? null)
  const ref = useRef(null)
  const prevActiveRef = useRef(null)
  const completionFiredRef = useRef(false)

  useEffect(() => {
    if (initialOpen && ref.current) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150)
    }
  }, [initialOpen])

  const phaseStates = PHASES.map(phase => ({
    ...phase,
    doneCount: phase.steps.filter(s => s.done(profile)).length,
    total: phase.steps.length,
    complete: phase.steps.every(s => s.done(profile)),
  }))

  const activeIndex = phaseStates.findIndex(p => !p.complete)

  // Fire onboarding completion once — give friend their Plus month
  useEffect(() => {
    if (activeIndex === -1 && !completionFiredRef.current && accessToken) {
      completionFiredRef.current = true
      fetch('/api/referral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: 'onboarding_complete' }),
      }).catch(() => {})
    }
  }, [activeIndex, accessToken])

  // When a phase completes, auto-open the next one
  useEffect(() => {
    if (prevActiveRef.current !== null && prevActiveRef.current !== activeIndex) {
      setExpandedPhase(null)
      setExpandedStep(null)
    }
    prevActiveRef.current = activeIndex
  }, [activeIndex])

  function isPhaseOpen(pi) {
    // Never lock a phase that contains the initialOpen step
    if (initialOpen != null && pi === initialPhaseIdx) return expandedPhase === null ? true : expandedPhase === pi
    if (activeIndex !== -1 && pi > activeIndex) return false // locked
    if (expandedPhase !== null) return expandedPhase === pi
    return pi === activeIndex // default: active phase open
  }

  return (
    <div ref={ref} className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {phaseStates.map((phase, pi) => {
        const isActive = pi === activeIndex
        const isLocked = activeIndex !== -1 && pi > activeIndex
        const isDone = phase.complete
        const phaseOpen = isPhaseOpen(pi)

        return (
          <div key={phase.id} className={pi > 0 ? 'border-t border-gray-100' : ''}>

            {/* Phase header */}
            <button
              type="button"
              disabled={isLocked}
              onClick={() => {
                setExpandedPhase(phaseOpen ? -1 : pi)
                setExpandedStep(null)
              }}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${!isLocked ? 'hover:bg-gray-50 active:bg-gray-100' : 'cursor-default'}`}
            >
              {/* Circle indicator */}
              {isDone ? (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                  <CheckIcon className="w-2.5 h-2.5 text-white" />
                </div>
              ) : isActive ? (
                <div className="w-5 h-5 rounded-full border-2 border-blue-500 flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-200 shrink-0" />
              )}

              {/* Title + subtitle */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold leading-snug ${isDone ? 'text-gray-400 line-through decoration-gray-300' : isLocked ? 'text-gray-300' : 'text-gray-800'}`}>
                  {phase.label}
                </p>
                {(isActive || isDone) && phaseOpen && phase.subtitle && (
                  <p className="text-xs text-gray-400 mt-0.5 font-normal">{phase.subtitle}</p>
                )}
                {isLocked && (
                  <p className="text-xs text-gray-300 mt-0.5 font-normal">Unlocks after "{phaseStates[activeIndex]?.label}"</p>
                )}
              </div>

              {/* Badge + chevron */}
              <div className="flex items-center gap-2 shrink-0">
                {isDone && <span className="text-xs font-medium bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Done</span>}
                {isActive && <span className="text-xs font-medium bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{phase.doneCount}/{phase.total}</span>}
                {isLocked && <span className="text-xs text-gray-300">{phase.total} steps</span>}
                {!isLocked && (
                  <svg className={`w-4 h-4 text-gray-300 transition-transform duration-200 ${phaseOpen ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>
            </button>

            {/* Steps list */}
            {phaseOpen && (
              <div className="border-t border-gray-100 bg-gray-50/50">
                {phase.steps.map((step, si) => {
                  const done = step.done(profile)
                  const open = expandedStep === step.id
                  const isLast = si === phase.steps.length - 1

                  return (
                    <div key={step.id} className={`${isLast ? '' : 'border-b border-gray-100'} ${open ? 'bg-white' : ''}`}>
                      {done ? (
                        <button
                          type="button"
                          onClick={() => setExpandedStep(open ? null : step.id)}
                          className="w-full flex items-center gap-3 pl-8 pr-4 py-2.5 text-left hover:bg-gray-100/60 transition-colors"
                        >
                          <div className="w-3.5 h-3.5 rounded-full bg-green-400 flex items-center justify-center shrink-0">
                            <CheckIcon className="w-2 h-2 text-white" />
                          </div>
                          <span className="text-sm flex-1 text-gray-400 line-through decoration-gray-200">{step.title}</span>
                          <span className="text-xs text-gray-300 hover:text-gray-500">{open ? 'Close' : 'Adjust →'}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setExpandedStep(open ? null : step.id)}
                          className="w-full flex items-center gap-3 pl-8 pr-4 py-2.5 text-left hover:bg-gray-100/60 active:bg-gray-100 transition-colors"
                        >
                          <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${open ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} />
                          <span className={`text-sm flex-1 ${open ? 'font-medium text-blue-700' : 'text-gray-700'}`}>{step.title}</span>
                          <svg className={`w-3.5 h-3.5 text-gray-300 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                      {open && (
                        <div className="px-4 pb-4 pl-12 border-t border-gray-100 pt-3">
                          <step.Form
                            profile={profile}
                            currentUser={currentUser}
                            accessToken={accessToken}
                            onUpdate={onUpdate}
                            onDone={() => setExpandedStep(null)}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

          </div>
        )
      })}
    </div>
  )
}
